import Seo from "../components/common/Seo";
import PageHeader from "../components/common/PageHeader";
import { Eyebrow, SectionHeading } from "../components/common/Type";

const palette = [
  { name: "White", hex: "#FFFFFF", ring: true },
  { name: "Off White", hex: "#F4F6F8" },
  { name: "Light Grey", hex: "#E5E7EB" },
  { name: "Cool Grey", hex: "#9AA4B2" },
  { name: "Neutral Grey", hex: "#6B7280" },
  { name: "Slate", hex: "#4B5563" },
  { name: "Near Black", hex: "#10131A" },
  { name: "Black", hex: "#000000" },
];

function Panel({ title, children }) {
  return (
    <div className="border border-kv-steel/20 rounded-[14px] overflow-hidden">
      <div className="px-6 py-3 border-b border-kv-steel/20 font-mono text-[10px] uppercase tracking-[0.18em] text-kv-royal">{title}</div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default function BrandReview() {
  return (
    <>
      <Seo title="Brand Review — Kratos Venture Group" description="Identity system and applications for Kratos Venture Group." path="/brand-review" />
      <PageHeader
        eyebrow="Internal · Brand Review"
        title="The Kratos identity system."
        intro="Canonical assets, colourways, applications, and specifications. The full company name is the primary identity — never reduced to a single letter or monogram."
      />

      {/* Lockups & colourways */}
      <section className="relative py-16" data-testid="brand-lockups">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow className="mb-8">Primary lockups & colourways</Eyebrow>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Panel title="Horizontal — on light (primary live treatment)">
              <div className="bg-white flex items-center justify-center py-12 border border-kv-steel/15"><img src="/brand/logo-horizontal-black.png" alt="Horizontal black" className="h-10 w-auto" /></div>
            </Panel>
            <Panel title="Horizontal — on black (inverse)">
              <div className="bg-black flex items-center justify-center py-12"><img src="/brand/logo-horizontal-white.png" alt="Horizontal white" className="h-10 w-auto" /></div>
            </Panel>
            <Panel title="Stacked — on light">
              <div className="bg-white flex items-center justify-center py-12 border border-kv-steel/15"><img src="/brand/logo-stacked-black.png" alt="Stacked black" className="h-24 w-auto" /></div>
            </Panel>
            <Panel title="Stacked — on black (inverse)">
              <div className="bg-black flex items-center justify-center py-12"><img src="/brand/logo-stacked-white.png" alt="Stacked white" className="h-24 w-auto" /></div>
            </Panel>
            <Panel title="Pivot mark — black on white">
              <div className="bg-white flex items-center justify-center py-12 border border-kv-steel/15"><img src="/brand/pivot-icon-black.png" alt="Pivot black" className="h-16 w-16" /></div>
            </Panel>
            <Panel title="Pivot mark — white on black">
              <div className="bg-black flex items-center justify-center py-12"><img src="/brand/pivot-icon-white.png" alt="Pivot white" className="h-16 w-16" /></div>
            </Panel>
          </div>
        </div>
      </section>

      {/* Controlled lockup treatments */}
      <section className="relative py-16 bg-kv-bg2 border-y border-kv-steel/20" data-testid="brand-treatments">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow className="mb-3">Controlled lockup treatments</Eyebrow>
          <p className="font-body text-sm text-kv-grey mb-8 max-w-2xl">Three arrangements built strictly from the existing mark — for review only. No alternative logo is proposed.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Panel title="Horizontal (standard)">
              <div className="bg-white flex items-center justify-center py-14 border border-kv-steel/15"><img src="/brand/logo-horizontal-black.png" alt="Treatment 1" className="h-8 w-auto" /></div>
            </Panel>
            <Panel title="Stacked (centered)">
              <div className="bg-white flex items-center justify-center py-14 border border-kv-steel/15"><img src="/brand/logo-stacked-black.png" alt="Treatment 2" className="h-20 w-auto" /></div>
            </Panel>
            <Panel title="Compact (tight / mobile)">
              <div className="bg-white flex items-center justify-center py-14 border border-kv-steel/15"><img src="/brand/logo-horizontal-black.png" alt="Treatment 3" className="h-6 w-auto" /></div>
            </Panel>
          </div>
        </div>
      </section>

      {/* Clear space & min size */}
      <section className="relative py-16" data-testid="brand-specs">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Panel title="Clear space">
            <div className="bg-white p-10 border border-kv-steel/15">
              <div className="border border-dashed border-kv-royal/50 p-8 flex items-center justify-center">
                <img src="/brand/logo-horizontal-black.png" alt="Clear space" className="h-8 w-auto" />
              </div>
              <p className="mt-4 font-body text-sm text-kv-grey">Maintain clear space equal to the height of the pivot mark on all sides. Keep other elements outside this boundary.</p>
            </div>
          </Panel>
          <Panel title="Minimum size">
            <div className="bg-white p-10 border border-kv-steel/15">
              <img src="/brand/logo-horizontal-black.png" alt="Minimum size" className="h-4 w-auto" />
              <p className="mt-6 font-body text-sm text-kv-grey">Do not render the horizontal lockup below 20px height (digital). Use the compact full-name lockup when space is tight — never a "K" or "KVG" monogram.</p>
            </div>
          </Panel>
        </div>
      </section>

      {/* Colour specs */}
      <section className="relative py-16 bg-kv-bg2 border-y border-kv-steel/20" data-testid="brand-palette">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow className="mb-8">Colour specification — strict monochrome</Eyebrow>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-kv-steel/20 border border-kv-steel/20">
            {palette.map((c) => (
              <div key={c.hex} className="bg-white p-5">
                <div className={`h-16 w-full mb-4 ${c.ring ? "border border-kv-steel/40" : ""}`} style={{ background: c.hex }} />
                <div className="font-display text-sm text-kv-off">{c.name}</div>
                <div className="font-mono text-[11px] text-kv-royal mt-1">{c.hex}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Typography specs */}
      <section className="relative py-16" data-testid="brand-typography">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow className="mb-8">Typography</Eyebrow>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Panel title="Display — IBM Plex Sans">
              <div className="font-display text-4xl text-kv-off tracking-tight">Aa</div>
              <p className="mt-4 font-body text-sm text-kv-grey">Headings and product statements. Medium weight, tight tracking.</p>
            </Panel>
            <Panel title="Body — IBM Plex Sans">
              <div className="font-body text-4xl text-kv-off">Aa</div>
              <p className="mt-4 font-body text-sm text-kv-grey">Legible corporate body copy across the site.</p>
            </Panel>
            <Panel title="Mono — IBM Plex Mono">
              <div className="font-mono text-4xl text-kv-off">Aa</div>
              <p className="mt-4 font-body text-sm text-kv-grey">Eyebrows, labels, and technical annotations.</p>
            </Panel>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="relative py-16 bg-kv-bg2 border-t border-kv-steel/20" data-testid="brand-applications">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading className="!text-3xl mb-10">Applications</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Panel title="Website header">
              <div className="bg-white border border-kv-steel/20 h-40 flex flex-col">
                <div className="flex items-center justify-between px-4 h-12 border-b border-kv-steel/20">
                  <img src="/brand/logo-horizontal-black.png" alt="" className="h-4 w-auto" />
                  <span className="font-mono text-[8px] uppercase tracking-wide text-kv-royal">Strategy Call</span>
                </div>
              </div>
            </Panel>
            <Panel title="Business card">
              <div className="bg-black h-40 p-5 flex flex-col justify-between">
                <img src="/brand/pivot-icon-white.png" alt="" className="h-8 w-8" />
                <div><img src="/brand/logo-horizontal-white.png" alt="" className="h-4 w-auto" /></div>
              </div>
            </Panel>
            <Panel title="Office signage">
              <div className="bg-white h-40 flex items-center justify-center border border-kv-steel/20">
                <img src="/brand/logo-stacked-black.png" alt="" className="h-20 w-auto" />
              </div>
            </Panel>
            <Panel title="Presentation cover">
              <div className="bg-gradient-to-br from-black to-kv-navy h-40 p-6 flex flex-col justify-end">
                <img src="/brand/logo-horizontal-white.png" alt="" className="h-5 w-auto" />
                <span className="mt-2 font-mono text-[9px] uppercase tracking-wide text-kv-steel">Business, reengineered.</span>
              </div>
            </Panel>
            <Panel title="Hardware engraving">
              <div className="relative h-40 overflow-hidden border border-kv-steel/20 bg-[#06070A]">
                <img src="/assets/device/device-assembled.jpg" alt="" className="absolute inset-0 h-full w-full object-contain" />
              </div>
            </Panel>
            <Panel title="Social profile & favicon">
              <div className="bg-white h-40 flex items-center justify-center gap-6 border border-kv-steel/20">
                <img src="/brand/favicon-192.png" alt="Favicon" className="h-16 w-16 rounded-2xl" />
                <img src="/brand/pivot-icon-black.png" alt="" className="h-10 w-10" />
              </div>
            </Panel>
          </div>
        </div>
      </section>
    </>
  );
}
